'use client';

import { useState } from 'react';
import { PlayerRef } from '@remotion/player';
import { Chip, LoadingSpinner } from '@crabs-ai/ds-react';
import { useTranslations } from 'next-intl';
import { useGetSharedAssetsApi } from '@waka/compat/bgm-catalog';
import BgmItem from './BgmItem';
import {
  BGM_TAG_OPTIONS,
  getSelectedBgmTagsQuery,
  toggleBgmTagSelection,
} from './bgm-tag-filter';

interface BgmContentProps {
  playerRef: React.RefObject<PlayerRef | null>;
}

export default function BgmContent({ playerRef }: BgmContentProps) {
  const t = useTranslations();
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  const {
    data: assetData,
    isLoading: isAssetLoading,
    isError,
    refetch,
    isFetching,
  } = useGetSharedAssetsApi(
    {
      tags: getSelectedBgmTagsQuery(selectedTags),
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: (previousData) => previousData,
    },
  );

  // shared-assets 응답은 기존 asset 목록과 달리 data/metaData 래퍼를 사용한다.
  const items = assetData?.data ?? [];
  const isInitialLoading = isAssetLoading && !assetData;
  const isAllSelected = selectedTags.length === 0;
  const emptyMessageKey = isAllSelected
    ? 'Editor.audio-panel.bgm-empty'
    : 'Editor.audio-panel.bgm-no-results';

  return (
    <div className="flex h-full min-h-0 w-full flex-col px-md layout-padding-bottom pb-sm">
      <div className="flex gap-2xs overflow-x-auto py-2xs">
        <div className="shrink-0">
          <Chip
            size="md"
            style="filled"
            selected={isAllSelected}
            labelNode={t('Editor.audio-panel.bgm-tag.all')}
            onClick={() => setSelectedTags([])}
          />
        </div>
        {BGM_TAG_OPTIONS.map((tagOption) => (
          <div
            key={tagOption.code}
            className="shrink-0"
          >
            <Chip
              size="md"
              style="filled"
              selected={selectedTags.includes(tagOption.code)}
              labelNode={t(tagOption.translationKey)}
              onClick={() =>
                setSelectedTags((previousTags) =>
                  toggleBgmTagSelection(previousTags, tagOption.code),
                )
              }
            />
          </div>
        ))}
      </div>

      {isError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2xs">
          <p className="text-sys-cont-neutral-tertiary text-center text-body-small">
            {t('Editor.audio-panel.bgm-load-fail')}
          </p>
          <button type="button" disabled={isFetching} onClick={() => void refetch()}>
            {isFetching ? '배경음 다시 불러오는 중…' : '배경음 목록 다시 불러오기'}
          </button>
        </div>
      ) : isInitialLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sys-cont-neutral-tertiary text-center text-body-small">
            {t(emptyMessageKey)}
          </p>
        </div>
      ) : (
        // 같은 작업의 목록을 현지 필터링하므로 태그 변경마다 다시 조회하지 않는다.
        <div className="flex min-h-0 flex-1 flex-col gap-2xs overflow-y-auto py-2xs">
          {items.map((item) => (
            <BgmItem
              key={item.contentId}
              item={item}
              playerRef={playerRef}
            />
          ))}
        </div>
      )}
    </div>
  );
}
